import React, { memo, useCallback, useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import '../../services/remote-setup';
import ErrorBoundary from '../../common/ui/error-boundary';
import { NO_OP } from '../../common/utils';
import useRemoteModule from '../../services/use-remote-module';
import { type DefaultOpenProps, type RemoteComponentProps } from '../../types';

const InternalRemoteComponent = ({
	analyticsChannel,
	remotePlugin,
	exportName = 'default',
	children = null,
	Fallback = null,
	...componentProps
}: Omit<RemoteComponentProps, 'ErrorFallback'>) => {
	const [, setError] = useState();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'rendered',
			actionSubject: 'remoteModule',
			actionSubjectId: 'remoteComponent',
			attributes: {
				exportName,
				dimensions: remotePlugin.dimensions,
				plugin: remotePlugin.constructor.name,
			},
		});

		analyticsEvent.fire(analyticsChannel);
	}, [analyticsChannel, createAnalyticsEvent, exportName, remotePlugin]);

	const onError = useCallback((error: Error) => {
		// this is a workaround to throw an error from an async function
		setError(() => {
			throw error;
		});
	}, []);

	const remoteFrontendSdk = useRemoteModule(remotePlugin, {
		analyticsChannel,
		onError,
		onEvent: componentProps.onEvent,
	});

	useEffect(() => {
		return () => {
			void remoteFrontendSdk.renderUfoExperience.abort({
				metadata: {
					moduleName: remoteFrontendSdk.getModuleName(),
				},
			});
		};
	}, [remoteFrontendSdk]);

	return (
		<>
			{remoteFrontendSdk.renderComponent(
				exportName,
				componentProps,
				Fallback,
				children,
				remoteFrontendSdk.renderUfoExperience,
				componentProps.onEvent,
			)}
		</>
	);
};

const RemoteComponent = <P extends {} = DefaultOpenProps>({
	analyticsChannel,
	remotePlugin,
	ErrorFallback = null,
	onEvent = NO_OP,
	...props
}: RemoteComponentProps<P>): React.JSX.Element => {
	useEffect(() => {
		return () => {
			onEvent({ type: 'unmount' });
		};
	}, [onEvent]);

	const onError = useCallback(
		(error: Error) => {
			onEvent({ error, type: 'render-error' });
		},
		[onEvent],
	);

	return (
		<ErrorBoundary
			analyticsChannel={analyticsChannel}
			remotePlugin={remotePlugin}
			onError={onError}
			fallback={ErrorFallback}
		>
			<InternalRemoteComponent
				analyticsChannel={analyticsChannel}
				remotePlugin={remotePlugin}
				onEvent={onEvent}
				{...props}
			/>
		</ErrorBoundary>
	);
};

export default memo(RemoteComponent) as typeof RemoteComponent;
